import {
  ALL_LEVELS_LABEL,
  PRESENTATION_FILTER_LABEL,
  RETIRED_LEVEL_LABEL,
} from '@/lib/persona-labels';
import { PERSONA_WORDING } from '@/lib/persona-wording';
import type { InvolvementLevel, PersonaFilters } from '@matter_extractor/shared';
import {
  PERSONA_LEVEL_FILTER_ALL,
  PERSONA_LEVEL_FILTER_RETIRED,
  personaPresentationFilterSchema,
} from '@matter_extractor/shared';
import { Select } from '@pms-digital/design-system';

/**
 * The persona's TWO own filters, at the head of « Plus de filtres » (refonte
 * modulaire §10, §15).
 *
 * The criterion puts its single « Exemples » select in this same slot, and
 * `filter-panel.tsx` was written naming this pair by hand: « the object's own
 * slot, first, because it is what somebody opened the panel for: the number of
 * examples for a criterion (§6), the presentation and the cran for a persona
 * (§10) ». §4 fixes the five ALWAYS-VISIBLE filters as the common ones, so an
 * object's own dimensions cannot be among them.
 *
 * The mockup draws them in the visible row instead, in place of « Protocole » -
 * a persona is chosen by an expérience and by no protocole (§10.2). That is a
 * form the layer cannot express without a second slot on `filter-bar.tsx`, and
 * §4's own sentence about the five is the fond. Noted in the MR rather than
 * silently resolved.
 *
 * ## The cran's options are ROWS, which is why the scale is a prop
 *
 * They are the crans of §11.5, which an operator renames, reorders and deletes.
 * No enum could be written once and stay true, so `personaLevelFilterSchema` is
 * a bare string and the two reserved values sit beside the identifiers: `all`,
 * the neutral state, and `retired`, which finds the personas whose cran has
 * left the scale. A cran identifier is a uuid, so neither word can collide.
 */

export interface PersonaFilterFieldsProps {
  filters: PersonaFilters;
  /** The scale as it stands, in order - the select's middle options. */
  levels: readonly InvolvementLevel[];
  onChange: (patch: Partial<PersonaFilters>) => void;
}

export function PersonaFilterFields({ filters, levels, onChange }: PersonaFilterFieldsProps) {
  // The test ids sit on wrappers: the package `Select` takes none (its trigger
  // is the `combobox` inside). Both neutral choices are words (`all`), so no
  // empty value has to cross the select.
  return (
    <>
      <div
        data-testid={`${PERSONA_WORDING.entity}-filtre-presentation`}
        className="w-40 max-w-full"
      >
        <Select
          snug
          label="Présentation"
          value={filters.presentation}
          onValueChange={(value) => {
            const parsed = personaPresentationFilterSchema.safeParse(value);
            if (parsed.success) onChange({ presentation: parsed.data });
          }}
          options={personaPresentationFilterSchema.options.map((option) => ({
            value: option,
            label: PRESENTATION_FILTER_LABEL[option],
          }))}
        />
      </div>

      <div data-testid={`${PERSONA_WORDING.entity}-filtre-cran`} className="w-45 max-w-full">
        {/*
          « Cran retiré » LAST, and after the scale rather than among it: the
          mockup detaches it with a separator, which the package select has no
          way to draw either. Its place at the foot says the same thing - it is
          not a cran, it is the question « which personas point at nothing any
          more ».
        */}
        <Select
          snug
          label="Cran"
          value={filters.level}
          onValueChange={(level) => onChange({ level })}
          options={[
            { value: PERSONA_LEVEL_FILTER_ALL, label: ALL_LEVELS_LABEL },
            ...levels.map((level) => ({ value: level.id, label: level.name })),
            { value: PERSONA_LEVEL_FILTER_RETIRED, label: RETIRED_LEVEL_LABEL },
          ]}
        />
      </div>
    </>
  );
}
